import { useState, useEffect } from "react";
import axios from "axios";
import {
  Users, PhoneCall, CheckCircle2, Calendar, ArrowUpRight, ArrowDownRight,
  Plus, ChevronRight, Eye, Rocket, UserPlus, Megaphone, Phone, X, Check
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, Line
} from "recharts";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function OnboardingGuide({ hasContacts, hasCampaigns, hasCalls, onDismiss, onNavigate }) {
  const steps = [
    {
      id: 1,
      title: "Kontakt hinzufügen",
      desc: "Importieren Sie Ihre Kontakte per CSV oder erstellen Sie den ersten manuell.",
      icon: UserPlus,
      done: hasContacts,
      action: () => onNavigate("/dashboard/contacts"),
      btnText: "Kontakte verwalten"
    },
    {
      id: 2,
      title: "Kampagne erstellen",
      desc: "Definieren Sie Ihr Skript und wählen Sie eine KI-Stimme für Ihre Anrufe.",
      icon: Megaphone,
      done: hasCampaigns,
      action: () => onNavigate("/dashboard/campaigns"),
      btnText: "Kampagne anlegen"
    },
    {
      id: 3,
      title: "Ersten Anruf starten",
      desc: "Starten Sie Ihren ersten KI-gesteuerten Anruf und verfolgen Sie das Ergebnis live.",
      icon: Phone,
      done: hasCalls,
      action: () => onNavigate("/dashboard/calls"),
      btnText: "Anruf starten"
    }
  ];

  const completed = steps.filter(s => s.done).length;
  const progress = Math.round((completed / steps.length) * 100);
  const allDone = completed === steps.length;

  return (
    <Card className="bg-white shadow-sm border border-slate-200/60 overflow-hidden" data-testid="onboarding-guide">
      <div className="bg-gradient-to-r from-[#405189] to-[#4b5c9e] px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
            <Rocket className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">
              {allDone ? "Onboarding abgeschlossen!" : "Willkommen bei quofon.de!"}
            </h3>
            <p className="text-xs text-white/70 mt-0.5">
              {allDone ? "Sie haben alle Schritte erfolgreich durchlaufen." : "Folgen Sie diesen Schritten, um loszulegen."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-24 h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-[#0ab39c] rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-xs font-semibold text-white/80">{completed}/{steps.length}</span>
          </div>
          <button onClick={onDismiss} className="text-white/50 hover:text-white transition-colors" data-testid="onboarding-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <CardContent className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {steps.map((step, i) => (
            <div
              key={step.id}
              className={`relative rounded-xl border p-4 transition-all ${
                step.done
                  ? "border-[#0ab39c]/30 bg-[#0ab39c]/5"
                  : i === completed
                    ? "border-[#405189]/30 bg-[#405189]/5 ring-1 ring-[#405189]/10"
                    : "border-slate-200 bg-slate-50/50"
              }`}
              data-testid={`onboarding-step-${step.id}`}
            >
              {/* Step number / check */}
              <div className="flex items-center justify-between mb-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  step.done
                    ? "bg-[#0ab39c] text-white"
                    : i === completed
                      ? "bg-[#405189] text-white"
                      : "bg-slate-200 text-slate-500"
                }`}>
                  {step.done ? <Check className="w-4 h-4" /> : step.id}
                </div>
                <step.icon className={`w-5 h-5 ${
                  step.done ? "text-[#0ab39c]" : i === completed ? "text-[#405189]" : "text-slate-400"
                }`} />
              </div>
              <h4 className={`text-sm font-semibold ${step.done ? "text-[#0ab39c]" : "text-slate-800"}`}>
                {step.done ? <span className="flex items-center gap-1">{step.title} <Check className="w-3.5 h-3.5" /></span> : step.title}
              </h4>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{step.desc}</p>
              {!step.done && (
                <Button
                  size="sm"
                  onClick={step.action}
                  className={`mt-3 text-xs h-8 gap-1 ${
                    i === completed
                      ? "bg-[#405189] hover:bg-[#354270] text-white"
                      : "bg-slate-200 text-slate-500 hover:bg-slate-300"
                  }`}
                  data-testid={`onboarding-action-${step.id}`}
                >
                  {step.btnText}
                  <ChevronRight className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState("ALL");
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem("onboarding_dismissed") === "true"
  );
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [statsRes, aptRes, campRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/appointments?status=scheduled`, { headers }),
        axios.get(`${API}/campaigns`, { headers })
      ]);
      setStats(statsRes.data);
      setAppointments(Array.isArray(aptRes.data) ? aptRes.data : []);
      setCampaigns(Array.isArray(campRes.data) ? campRes.data : []);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const userName = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}").name || "Benutzer"; }
    catch { return "Benutzer"; }
  })();

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Guten Morgen";
    if (h < 18) return "Guten Tag";
    return "Guten Abend";
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-14 bg-slate-200 rounded-lg" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-slate-200 rounded-lg" />)}
        </div>
        <div className="grid grid-cols-5 gap-4">
          <div className="col-span-3 h-80 bg-slate-200 rounded-lg" />
          <div className="col-span-2 h-80 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: "GESAMTANRUFE",
      value: stats?.total_calls || 0,
      change: "+16.24%",
      isPositive: true,
      icon: PhoneCall,
      iconBg: "bg-[#daf4f0]",
      iconColor: "text-[#0ab39c]",
      link: "Alle Anrufe ansehen",
      linkHref: "/dashboard/calls"
    },
    {
      title: "KONTAKTE",
      value: stats?.total_contacts || 0,
      change: "+29.08%",
      isPositive: true,
      icon: Users,
      iconBg: "bg-[#d6e4ff]",
      iconColor: "text-[#405189]",
      link: "Alle Kontakte ansehen",
      linkHref: "/dashboard/contacts"
    },
    {
      title: "ERFOLGSRATE",
      value: `${stats?.success_rate || 0}%`,
      change: stats?.success_rate > 10 ? "+5.02%" : "-3.57%",
      isPositive: stats?.success_rate > 10,
      icon: CheckCircle2,
      iconBg: "bg-[#fef4e4]",
      iconColor: "text-[#f7b84b]",
      link: "Details ansehen",
      linkHref: "/dashboard/history"
    },
    {
      title: "TERMINE",
      value: appointments.length,
      change: "+0.00%",
      isPositive: true,
      icon: Calendar,
      iconBg: "bg-[#e8daff]",
      iconColor: "text-[#6559cc]",
      link: "Termine verwalten",
      linkHref: "/dashboard/appointments"
    }
  ];

  const chartData = stats?.calls_per_day || [];
  const totalContacts = Math.max(stats?.total_contacts || 1, 1);

  const statusData = [
    { label: "Neu", count: stats?.by_status?.new || 0, color: "#405189" },
    { label: "Angerufen", count: stats?.by_status?.called || 0, color: "#0ab39c" },
    { label: "Erfolg", count: stats?.by_status?.success || 0, color: "#f7b84b" },
    { label: "Rückruf", count: stats?.by_status?.callback || 0, color: "#299cdb" },
    { label: "Abgelehnt", count: stats?.by_status?.rejected || 0, color: "#f06548" },
  ];

  // Build donut chart segments
  const totalForDonut = statusData.reduce((s, d) => s + d.count, 0) || 1;
  let donutOffset = 0;
  const donutSegments = statusData.map(d => {
    const pct = (d.count / totalForDonut) * 100;
    const segment = { ...d, pct, offset: donutOffset };
    donutOffset += pct;
    return segment;
  });

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="dashboard-page">
      {/* ===== MAIN CONTENT (9 cols on xl) ===== */}
      <div className="col-span-12 xl:col-span-9 space-y-4">

        {/* Greeting Row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-800" data-testid="dashboard-title">
              {getGreeting()}, {userName}!
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Hier ist die aktuelle Übersicht Ihres quofon.de Dialers.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded text-xs text-slate-600">
              <Calendar className="w-3.5 h-3.5" />
              <span>{new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}</span>
            </div>
            <Button size="sm" className="bg-[#0ab39c] hover:bg-[#099b87] text-white text-xs gap-1.5" data-testid="add-contact-btn">
              <Plus className="w-3.5 h-3.5" />
              Neuer Kontakt
            </Button>
          </div>
        </div>

        {/* Onboarding Guide */}
        {!onboardingDismissed && (
          <OnboardingGuide
            hasContacts={(stats?.total_contacts || 0) > 0}
            hasCampaigns={campaigns.length > 0}
            hasCalls={(stats?.total_calls || 0) > 0}
            onDismiss={() => {
              setOnboardingDismissed(true);
              localStorage.setItem("onboarding_dismissed", "true");
            }}
            onNavigate={navigate}
          />
        )}

        {/* KPI Cards Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((card, i) => (
            <Card key={card.title} className="bg-white shadow-sm border border-slate-200/60" data-testid={`kpi-card-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{card.title}</span>
                  <span className={`text-[11px] font-semibold flex items-center gap-0.5 ${card.isPositive ? "text-[#0ab39c]" : "text-[#f06548]"}`}>
                    {card.isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {card.change}
                  </span>
                </div>
                <div className="flex items-end justify-between">
                  <h3 className="text-[22px] font-bold text-slate-800 leading-none">{card.value}</h3>
                  <div className={`w-10 h-10 rounded-lg ${card.iconBg} flex items-center justify-center`}>
                    <card.icon className={`w-[18px] h-[18px] ${card.iconColor}`} />
                  </div>
                </div>
                <a href={card.linkHref} className="text-[11px] text-[#405189] hover:text-[#2d3a5e] mt-3 inline-flex items-center gap-1 font-medium underline underline-offset-2 decoration-dashed">
                  {card.link}
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Revenue Chart + Status Overview Row */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Chart Card - 3 cols */}
          <Card className="lg:col-span-3 bg-white shadow-sm border border-slate-200/60" data-testid="revenue-chart">
            <CardHeader className="pb-0 pt-4 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800">Anruf-Statistik</CardTitle>
                <div className="flex items-center gap-1">
                  {["ALL", "1W", "1M", "6M"].map(p => (
                    <button
                      key={p}
                      onClick={() => setChartPeriod(p)}
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors ${
                        chartPeriod === p
                          ? "bg-[#405189] text-white border-[#405189]"
                          : "text-slate-500 border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`chart-filter-${p}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-3">
              {/* Stat boxes above chart */}
              <div className="grid grid-cols-5 gap-3 mb-4 pb-3 border-b border-slate-100">
                {[
                  { val: stats?.total_calls || 0, label: "Anrufe" },
                  { val: stats?.successful_calls || 0, label: "Erfolge" },
                  { val: stats?.by_status?.rejected || 0, label: "Abgelehnt" },
                  { val: `${stats?.success_rate || 0}%`, label: "Conversion", isGreen: true },
                  { val: `${(stats?.total_cost || 0).toFixed(2)} €`, label: "Kosten" },
                ].map(s => (
                  <div key={s.label}>
                    <p className={`text-base font-bold ${s.isGreen ? "text-[#0ab39c]" : "text-slate-800"}`}>{s.val}</p>
                    <p className="text-[10px] text-slate-500">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Chart */}
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "11px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                    />
                    <Bar dataKey="calls" name="Anrufe" fill="#405189" radius={[3, 3, 0, 0]} barSize={18} />
                    <Line type="monotone" dataKey="calls" name="Trend" stroke="#f7b84b" strokeWidth={2} dot={false} strokeDasharray="5 5" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-5 mt-2 text-[10px] text-slate-500">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#405189]" />Anrufe</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#0ab39c]" />Erfolge</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#f06548]" />Abgelehnt</span>
              </div>
            </CardContent>
          </Card>

          {/* Kontakte nach Status (like Sales by Locations) - 2 cols */}
          <Card className="lg:col-span-2 bg-white shadow-sm border border-slate-200/60" data-testid="status-overview">
            <CardHeader className="pb-0 pt-4 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800">Kontakte nach Status</CardTitle>
                <Button variant="outline" size="sm" className="text-[10px] h-6 px-2 font-medium" data-testid="export-btn">
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-3">
              {/* Donut Chart */}
              <div className="flex items-center justify-center mb-5">
                <div className="relative w-36 h-36">
                  <svg viewBox="0 0 42 42" className="w-full h-full -rotate-90">
                    <circle cx="21" cy="21" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="4" />
                    {donutSegments.map(seg => (
                      <circle
                        key={seg.label}
                        cx="21" cy="21" r="15.9"
                        fill="none"
                        stroke={seg.color}
                        strokeWidth="4"
                        strokeDasharray={`${seg.pct} ${100 - seg.pct}`}
                        strokeDashoffset={`-${seg.offset}`}
                        strokeLinecap="round"
                      />
                    ))}
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-800">{stats?.total_contacts || 0}</p>
                      <p className="text-[9px] text-slate-500 uppercase tracking-wider">Gesamt</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status progress bars */}
              <div className="space-y-3">
                {statusData.map(s => {
                  const pct = Math.round((s.count / totalContacts) * 100);
                  return (
                    <div key={s.label}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-600">{s.label}</span>
                        <span className="font-semibold text-slate-700">{pct}%</span>
                      </div>
                      <div className="h-[5px] bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: s.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Letzte Anrufe (like Best Selling Products) */}
          <Card className="bg-white shadow-sm border border-slate-200/60" data-testid="recent-calls-table">
            <CardHeader className="pb-0 pt-4 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800">Letzte Anrufe</CardTitle>
                <span className="text-[10px] text-slate-500"><b className="text-slate-700">SORTIERT:</b> Heute ▾</span>
              </div>
            </CardHeader>
            <CardContent className="p-0 mt-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-y border-slate-100 bg-slate-50/50">
                    <th className="text-left py-2 px-4 font-medium text-slate-500">Kontakt</th>
                    <th className="text-left py-2 px-4 font-medium text-slate-500">Kampagne</th>
                    <th className="text-left py-2 px-4 font-medium text-slate-500">Status</th>
                    <th className="text-left py-2 px-4 font-medium text-slate-500">Dauer</th>
                    <th className="text-left py-2 px-4 font-medium text-slate-500">Kosten</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.recent_calls || []).slice(0, 5).map((call, i) => (
                    <tr key={call.id || i} className="border-b border-slate-50 hover:bg-slate-50/40">
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-semibold text-slate-600 shrink-0">
                            {call.contact_name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-700">{call.contact_name}</p>
                            <p className="text-[9px] text-slate-400">{call.contact_phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-slate-600">{call.campaign_name}</td>
                      <td className="py-2.5 px-4">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                          call.status === "completed" ? "bg-[#daf4f0] text-[#0ab39c]" :
                          call.status === "initiated" ? "bg-[#d6e4ff] text-[#405189]" :
                          call.status === "failed" ? "bg-[#fde8e4] text-[#f06548]" :
                          "bg-slate-100 text-slate-500"
                        }`}>
                          {call.status}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-slate-600 font-mono">
                        {call.duration > 0 ? `${Math.floor(call.duration/60)}:${(call.duration%60).toString().padStart(2,'0')}` : "–"}
                      </td>
                      <td className="py-2.5 px-4 font-semibold text-slate-700">
                        {call.duration > 0 ? `${(Math.ceil(call.duration/60)*0.10).toFixed(2)} €` : "–"}
                      </td>
                    </tr>
                  ))}
                  {(!stats?.recent_calls || stats.recent_calls.length === 0) && (
                    <tr><td colSpan={5} className="py-8 text-center text-slate-400">Noch keine Anrufe vorhanden</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Top Kampagnen (like Top Sellers) */}
          <Card className="bg-white shadow-sm border border-slate-200/60" data-testid="top-campaigns-table">
            <CardHeader className="pb-0 pt-4 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800">Top Kampagnen</CardTitle>
                <span className="text-[10px] text-slate-500">Bericht ▾</span>
              </div>
            </CardHeader>
            <CardContent className="p-0 mt-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-y border-slate-100 bg-slate-50/50">
                    <th className="text-left py-2 px-4 font-medium text-slate-500">Kampagne</th>
                    <th className="text-left py-2 px-4 font-medium text-slate-500">Status</th>
                    <th className="text-left py-2 px-4 font-medium text-slate-500">Voice</th>
                    <th className="text-left py-2 px-4 font-medium text-slate-500">Erstellt</th>
                  </tr>
                </thead>
                <tbody>
                  {(campaigns || []).slice(0, 5).map((c) => (
                    <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/40">
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#e8daff] flex items-center justify-center text-[10px] font-semibold text-[#6559cc] shrink-0">
                            {c.name?.charAt(0)}
                          </div>
                          <p className="font-medium text-slate-700">{c.name}</p>
                        </div>
                      </td>
                      <td className="py-2.5 px-4">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold ${c.is_active ? "bg-[#daf4f0] text-[#0ab39c]" : "bg-slate-100 text-slate-500"}`}>
                          {c.is_active ? "Aktiv" : "Inaktiv"}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-slate-600 truncate max-w-[80px]">{c.voice_id?.slice(0, 8)}...</td>
                      <td className="py-2.5 px-4 text-slate-500">{new Date(c.created_at).toLocaleDateString("de-DE")}</td>
                    </tr>
                  ))}
                  {campaigns.length === 0 && (
                    <tr><td colSpan={4} className="py-8 text-center text-slate-400">Noch keine Kampagnen</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===== RIGHT SIDEBAR PANEL (3 cols on xl) ===== */}
      <div className="col-span-12 xl:col-span-3 space-y-4">
        {/* Recent Activity */}
        <Card className="bg-white shadow-sm border border-slate-200/60" data-testid="recent-activity">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-[11px] font-bold text-slate-700 uppercase tracking-widest">
              Letzte Aktivität
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-0">
              {(stats?.recent_calls || []).slice(0, 5).map((call, i) => (
                <div key={call.id || i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                      call.response === "accepted" ? "bg-[#daf4f0]" :
                      call.response === "callback" ? "bg-[#fef4e4]" :
                      call.response === "rejected" ? "bg-[#fde8e4]" :
                      "bg-[#d6e4ff]"
                    }`}>
                      <PhoneCall className={`w-3 h-3 ${
                        call.response === "accepted" ? "text-[#0ab39c]" :
                        call.response === "callback" ? "text-[#f7b84b]" :
                        call.response === "rejected" ? "text-[#f06548]" :
                        "text-[#405189]"
                      }`} />
                    </div>
                    {i < 4 && <div className="w-px flex-1 bg-slate-100 my-1" />}
                  </div>
                  <div className="pb-3 min-w-0">
                    <p className="text-[11px] font-semibold text-slate-700 truncate">Anruf bei {call.contact_name}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{call.campaign_name}</p>
                    <p className="text-[9px] text-slate-400 mt-1">
                      {new Date(call.created_at).toLocaleString("de-DE", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                    </p>
                  </div>
                </div>
              ))}
              {(!stats?.recent_calls || stats.recent_calls.length === 0) && (
                <p className="text-[11px] text-slate-400 text-center py-6">Keine Aktivitäten</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top 10 Categories → Kontakt-Übersicht */}
        <Card className="bg-white shadow-sm border border-slate-200/60" data-testid="top-categories">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-[11px] font-bold text-slate-700 uppercase tracking-widest">
              Kontakt-Übersicht
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-1.5">
              {[
                { label: "Neue Kontakte", val: stats?.by_status?.new || 0 },
                { label: "Angerufen", val: stats?.by_status?.called || 0 },
                { label: "Erfolgreiche Leads", val: stats?.by_status?.success || 0 },
                { label: "Rückruf ausstehend", val: stats?.pending_callbacks || 0 },
                { label: "Abgelehnt", val: stats?.by_status?.rejected || 0 },
                { label: "Anrufe heute", val: stats?.calls_today || 0 },
                { label: "Gesamtanrufe", val: stats?.total_calls || 0 },
                { label: "Erfolgsrate", val: `${stats?.success_rate || 0}%` },
                { label: "Aktive Kampagnen", val: campaigns.filter(c => c.is_active).length },
                { label: "Geplante Termine", val: appointments.length },
              ].map((item, i) => (
                <div key={item.label} className="flex items-center justify-between py-1">
                  <span className="text-[11px] text-slate-600">{i + 1}. {item.label}</span>
                  <span className="text-[11px] font-semibold text-slate-700">({item.val})</span>
                </div>
              ))}
            </div>
            <a href="/dashboard/history" className="block w-full text-center text-[11px] text-[#405189] hover:text-[#2d3a5e] font-medium mt-3 pt-3 border-t border-slate-100">
              Alle Statistiken ansehen
            </a>
          </CardContent>
        </Card>

        {/* Product Reviews → Nächste Termine */}
        <Card className="bg-white shadow-sm border border-slate-200/60" data-testid="upcoming-appointments">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-[11px] font-bold text-slate-700 uppercase tracking-widest">
              Nächste Termine
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-3">
              {(appointments || []).slice(0, 3).map((apt) => (
                <div key={apt.id} className="p-3 bg-[#f3f6f9] rounded-lg">
                  <div className="flex items-start gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-[#e8daff] flex items-center justify-center shrink-0 mt-0.5">
                      <Calendar className="w-3 h-3 text-[#6559cc]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-slate-700">{apt.contact_name}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {apt.appointment_type === "phone" ? "Telefontermin" :
                         apt.appointment_type === "video" ? "Videotermin" : "Vor-Ort-Termin"}
                      </p>
                      <p className="text-[9px] text-slate-400 mt-1">{apt.scheduled_date} • {apt.scheduled_time} Uhr</p>
                      {apt.notes && (
                        <p className="text-[9px] text-slate-400 mt-1 italic line-clamp-2">"{apt.notes}"</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {appointments.length === 0 && (
                <p className="text-[11px] text-slate-400 text-center py-6">Keine anstehenden Termine</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
